/* eslint-disable max-lines-per-function */
/* eslint-disable no-param-reassign */
/* eslint-disable default-case */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable sonarjs/cognitive-complexity */
import { type Layer, type HXKLineLayerSpec, defineHXKLineLayer } from '../../../core/layer';
import type { StockData } from '../../../helper/hxKLine';
import type { KLineTooltipItem } from '../../../ui/kLineTooltip/KLineTooltip';
import type { ExtensionApi } from '../../../core/extension';
import { defaultToken as defaultDataZoomToken } from '../../../ui/customDataZoom/DataZoom';
import { validateHXKlineComponents } from '../../../util/hxKLine';

// HXKLine数据接口定义
interface HXKLineData {
  timestamp: number;
  open: number;
  close: number;
  high: number;
  low: number;
  volume: number;
}

// HXKLine回调数据接口
interface HXKLineCallbackData {
  data: HXKLineData[];
  kLineData?: HXKLineData;
  dataIndex?: number;
}

// 标签覆盖物接口
interface TagOverlay {
  id: number;
  beginTimestamp: number;
  endTimestamp: number;
  borderColor: string;
  backgroundColor: string;
  titleList: unknown;
  hoverBackgroundColor: string;
  activeBackgroundColor: string;
  activeBorderColor: string;
  textSize: number;
  hoverBorderColor: string;
  isActive: boolean;
  isHover: boolean;
  onClick: (event: { id: number }) => void;
}

// DOM元素集合接口
interface DomElements {
  $toolbarLegend: HTMLElement;
  $tooltipContainer: HTMLElement;
  $chart: HTMLElement;
  $kline: HTMLElement;
  $dataZoomContainer: HTMLElement;
}

// 标签样式接口
interface TagToken {
  text: {
    color: string;
    fontSize: number;
  };
  rect: {
    borderColor: string;
    backgroundColor: string;
    hoverBackgroundColor: string;
    activeBackgroundColor: string;
    activeBorderColor: string;
    hoverBorderColor: string;
  };
  option?: unknown;
}

// 图例项接口
interface LegendItem {
  name: string;
  color: string;
  type: string | number;
  selected: boolean;
  disableSelect: boolean;
  dom: HTMLElement;
}

// 工具接口
interface Utils {
  common: {
    generateUID: () => string;
  };
  data: {
    formatDate: (timestamp: number) => string;
    deepMerge: <T>(source: T, target: unknown, options: { override: boolean }) => T;
  };
  const: {
    LAYOUT: {
      SMALL: string;
      SMALL_1_2: string;
    };
    SERIES_2_LEGEND_TYPE_MAP: {
      bar: string | number;
    };
  };
}

interface LegendInstance {
  destroy?: () => void;
  klineDropdown?: Array<{
    on: (event: 'change', callback: (callbackEvent: { key: string }) => void) => void;
  }>;
}

// UI组件接口
interface UI {
  Legend: {
    Legend: new (
      container: HTMLElement,
      options: { data: unknown[]; layout: string; token: unknown }
    ) => LegendInstance;
    ShowType: {
      Line: number;
      Rect: number;
    };
  };
  KLineTooltip: new (
    container: HTMLElement,
    options: { token: unknown }
  ) => {
    show: (data: KLineTooltipItem[]) => void;
    destroy: () => void;
  };
  CustomDataZoom: new (
    container: HTMLElement,
    options: { data: string[]; token: unknown; style: unknown }
  ) => {
    update: (data: string[]) => void;
    on: (event: string, callback: (startIndex: number, endIndex: number) => void) => void;
    destroy: () => void;
  };
}

// Hook接口
interface Hook {
  globalConfig: (api: ExtensionApi) => { hxKLineVerifyInfo: unknown };
  buildStandardChartPresetOption: (api: ExtensionApi) => { color?: string[] };
  getHXKLineTooltipData: (
    kLineIns: unknown,
    type: string,
    dataIndex?: number
  ) => { defaultTooltipData: KLineTooltipItem[] };
}

// HXKLine实例接口
interface HXKLineInstance {
  destroy: () => void;
  setZoomEnabled: (enabled: boolean) => void;
  setScrollEnabled: (enabled: boolean) => void;
  setBarSpaceLimit: (limit: { min: number; max: number }) => void;
  zoomToShowAllData: () => void;
  createOverlay: (options: { name: string; extendData: unknown[]; points: unknown[] }) => void;
  setPaneOptions: (options: {
    id: string;
    gap: { top: number; bottom: number };
    axisOptions: { scrollZoomEnabled: boolean };
  }) => void;
  subscribeAction: (action: string, onAction: (data: HXKLineCallbackData) => void) => void;
  applyNewData: (data: HXKLineData[], update: boolean, onUpdate: () => void) => void;
  getDataList: () => HXKLineData[];
  _chartContainer: HTMLElement;
}

// 声明 HXKline 和 HXKlineChart 的类型
declare const HXKline: {
  setHXKlineChart: (chart: unknown) => void;
  verifyProjectPermission: (info: unknown) => void;
  setGlobalConfig: (config: { isForbiddenLoadMore: boolean }) => void;
  setProjectMode: (mode: string) => void;
  disposeChart: (id: string | null, callback: (...args: unknown[]) => void) => void;
  initChart: (
    id: string,
    dataSource: {
      id: string;
      code: string;
      market: string;
      data_class: string;
      time_period: string;
      trade_date: Date[] | number;
      adjust_type: string;
      subscribe_type?: string;
      isForbiddenLoadMore?: boolean;
      isForbiddenUpdate?: boolean;
    },
    chartOptions: {
      chartType: string;
      dateFormat: string;
      locale: string;
      styles: unknown[];
    },
    onComplete: (status: 'success', data: HXKLineCallbackData) => void
  ) => HXKLineInstance;
};

declare const HXKlineChart: unknown;

validateHXKlineComponents();
HXKline.setHXKlineChart(HXKlineChart);

// 可视化组件信息
const visInfo: Layer['visInfo'] = {
  id: 'hxKLineAndTag',
  name_en: 'Candlestick Chart And Tag',
  // name_zh: '行情图叠加标签',
  name_zh: 'Candlestick Chart And Tag',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  parameters: {},
  extra: {
    isNotTrainedByLLM: true
  }
};

// DataZoom 配置接口
interface DataZoomConfig {
  type?: 'slider' | 'inside';
  startValue?: number | string;
  endValue?: number;
  moveOnMouseMove?: boolean;
  zoomOnMouseWheel?: boolean;
  dvScrollColor?: string;
  height?: number;
}

// 接口定义
export interface Spec extends HXKLineLayerSpec {
  /** 标签使用的数据集索引，默认为 0 */
  dataIndex?: number;
  /** 标签数据编码 */
  encoding: {
    from: string;
    to: string;
    concepts: string;
  };
  stocks: StockData[];
  tradeDate?: string[];
  customEvent?: (event: unknown) => void;
  showLegend?: boolean;
  showTooltip?: boolean;
  dataZoom?: DataZoomConfig;
}

interface DataSourceValue {
  [key: string]: string | number | Date;
}

interface DataSource {
  values: DataSourceValue[];
}

interface KLineTooltipInstance {
  show: (data: KLineTooltipItem[]) => void;
  destroy: () => void;
}

interface TokenStyles {
  dvHXKLine?: {
    constants?: {
      DEFAULT_TICK_LINE_COLOR?: string;
    };
    hxKLineAndTag?: unknown;
    dataZoom?: {
      height?: number;
    };
  };
}

// 辅助函数集
const helpers = {
  // 创建DOM元素
  createDomElements(utils: Utils, tokenStyles: TokenStyles, dataZoom: DataZoomConfig): DomElements {
    const $toolbarLegend = document.createElement('section');
    const $tooltipContainer = document.createElement('section');
    const $chart = document.createElement('section');
    const $kline = document.createElement('section');
    const $dataZoomContainer = document.createElement('section');

    const lineColor = tokenStyles?.dvHXKLine?.constants?.DEFAULT_TICK_LINE_COLOR;
    const borderLineStyle = `1px solid ${lineColor || 'rgba(125, 133, 146, 0.3)'}`;
    const defaultMarginTop = 8;
    const fallbackDataZoomHeight = 16;

    // 获取 dataZoom 的 height，确保有默认值
    const dataZoomHeight =
      dataZoom?.height ?? defaultDataZoomToken.height ?? fallbackDataZoomHeight;

    $toolbarLegend.style.cssText = 'width: 100%; margin-bottom: 8px;';
    $chart.style.cssText = `position: relative; flex: 1; width: 100%; height: calc(100% - ${
      dataZoomHeight + defaultMarginTop
    }px); display: flex; flex-direction: row;`;
    $kline.style.cssText = `
      position: relative;
      flex: 1;
      width: 0;
      height; 100%;
      border-right: ${borderLineStyle};
      border-top: ${borderLineStyle};
    `;
    $kline.setAttribute('id', utils.common.generateUID());

    return {
      $toolbarLegend,
      $tooltipContainer,
      $chart,
      $kline,
      $dataZoomContainer
    };
  },

  // 设置DOM结构
  setupDomStructure($container: HTMLElement, elements: DomElements): void {
    const { $toolbarLegend, $tooltipContainer, $chart, $kline, $dataZoomContainer } = elements;

    $container.style.cssText +=
      'display: flex; flex-direction: column; justify-content: center; align-items: center;';

    $chart.appendChild($kline);
    $container.appendChild($toolbarLegend);
    $container.appendChild($tooltipContainer);
    $container.appendChild($chart);
    $container.appendChild($dataZoomContainer);
  },

  // 设置标签样式
  setupTagToken(utils: Utils, tokenStyles: TokenStyles): TagToken {
    const defaultToken: TagToken = {
      text: {
        color: 'white',
        fontSize: 12
      },
      rect: {
        borderColor: 'rgba(128, 128, 128, 0.7)',
        backgroundColor: 'rgba(128, 128, 128, 0.1)',
        hoverBackgroundColor: 'rgba(128, 128, 128, 0.3)',
        activeBackgroundColor: 'rgba(128, 128, 128, 0.5)',
        activeBorderColor: 'rgba(128, 128, 128, 0.5)',
        hoverBorderColor: 'rgba(128, 128, 128, 0.3)'
      }
    };

    return utils.data.deepMerge(defaultToken, tokenStyles?.dvHXKLine?.hxKLineAndTag ?? {}, {
      override: true
    });
  },

  // 创建标签覆盖物列表
  createRiseCycleList(
    dataSource: DataSource,
    fromField: string,
    toField: string,
    conceptsField: string,
    tagStyles: TagToken,
    onCustomEvent?: (event: unknown) => void
  ): TagOverlay[] {
    return dataSource.values.map((item: DataSourceValue, index: number) => {
      const begin = new Date(item[fromField]);
      begin.setHours(0, 0, 0, 0);
      const end = new Date(item[toField]);
      end.setHours(0, 0, 0, 0);

      return {
        id: index,
        beginTimestamp: begin.getTime(),
        endTimestamp: end.getTime(),
        borderColor: tagStyles.rect.borderColor,
        backgroundColor: tagStyles.rect.backgroundColor,
        titleList: item[conceptsField],
        hoverBackgroundColor: tagStyles.rect.hoverBackgroundColor,
        activeBackgroundColor: tagStyles.rect.activeBackgroundColor,
        activeBorderColor: tagStyles.rect.activeBorderColor,
        textSize: tagStyles.text.fontSize,
        hoverBorderColor: tagStyles.rect.hoverBorderColor,
        isActive: index === dataSource.values.length - 1,
        isHover: false,
        onClick: (event: { id: number }) => {
          if (typeof onCustomEvent === 'function') {
            onCustomEvent(dataSource.values[event.id]);
          }
        }
      };
    });
  },

  // 创建图例数据
  createLegendData(
    dataSource: DataSource,
    conceptsField: string,
    colors: string[],
    utils: Utils,
    ui: UI
  ): LegendItem[] {
    const legendItems = dataSource.values
      .map((value: DataSourceValue) => {
        return value[conceptsField];
      })
      .flat()
      .map((item: unknown, index: number) => ({
        name: String(item),
        color: colors[index % colors.length],
        type: utils.const.SERIES_2_LEGEND_TYPE_MAP.bar,
        selected: true,
        disableSelect: true,
        dom: document.createElement('div')
      }));

    legendItems.push({
      name: 'K线',
      color: '#000000',
      type: ui.Legend.ShowType.Rect,
      selected: true,
      disableSelect: true,
      dom: document.createElement('div')
    });

    return legendItems;
  },

  // 销毁HXKLine
  disposeHXKLine(kLineInstance: HXKLineInstance): void {
    HXKline.disposeChart(
      kLineInstance._chartContainer.parentElement?.getAttribute('id') ?? null,
      () => {
        // HXKLine destroyed
      }
    );
  },

  // 配置HXKLine基本参数
  configureHXKLine(kLineInstance: HXKLineInstance): void {
    kLineInstance.setZoomEnabled(false);
    kLineInstance.setScrollEnabled(false);
    kLineInstance.zoomToShowAllData();
  },

  // 优化DOM元素
  optimizeDomElements(kLineContainer: HTMLElement): void {
    const $xAxis = kLineContainer.querySelector(
      'div > div:nth-child(2) > div:nth-child(1)'
    ) as HTMLElement;
    const $yAxis = kLineContainer.querySelector('div > div > div:nth-child(2)') as HTMLElement;

    if ($xAxis) {
      $xAxis.style.cursor = 'default';
    }
    if ($yAxis) {
      $yAxis.style.cursor = 'default';
    }
  },

  // 配置主图参数
  configureMainPane(kLineInstance: HXKLineInstance): void {
    kLineInstance.setPaneOptions({
      id: 'candle_pane',
      gap: {
        top: 0.05,
        bottom: 0.05
      },
      axisOptions: { scrollZoomEnabled: false }
    });
  },

  // 显示K线图Tooltip
  showKLineTooltip(
    kLineIns: unknown,
    hook: Hook,
    layout: string,
    tooltipInstance: KLineTooltipInstance,
    layoutConstants: { SMALL: string; SMALL_1_2: string },
    dataIndex?: number
  ): void {
    const result = hook.getHXKLineTooltipData(kLineIns, 'kline', dataIndex);
    const tooltipData = result.defaultTooltipData;

    if (!tooltipInstance) {
      return;
    }

    const SMALL_LAYOUT_TOOLTIP_FIELDS_COUNT = 3;

    if (layout === layoutConstants.SMALL || layout === layoutConstants.SMALL_1_2) {
      // 设计师规定，卡片宽度较小时只显示三项tooltip字段
      tooltipInstance.show(tooltipData.slice(0, SMALL_LAYOUT_TOOLTIP_FIELDS_COUNT));
    } else {
      tooltipInstance.show(tooltipData);
    }
  }
};

/**
 * K线图叠加标签图层
 * 暂不支持legend的点击功能
 */
export const hxKLineAndTag = defineHXKLineLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { layout, data: dataArray, hook, util, ui, token } = api;

    // 初始化HXKline
    HXKline.verifyProjectPermission(hook.globalConfig(api).hxKLineVerifyInfo);
    HXKline.setProjectMode('ChinaCache');
    HXKline.setGlobalConfig({
      // 禁止加载更多数据
      isForbiddenLoadMore: true
    });

    // 解析配置
    const {
      dataIndex = 0,
      encoding: { from, to, concepts },
      tradeDate,
      stocks,
      showLegend = false,
      customEvent,
      dataZoom = {},
      showTooltip = true
    } = spec;

    // 获取预设选项和数据
    const presetOption = hook.buildStandardChartPresetOption(api);
    const data = dataArray[dataIndex];
    const colorMap = presetOption?.color ?? [];

    // 初始化基础元素
    const tokenDataZoomHeight = (token?.dvHXKLine?.dataZoom as { height?: number })?.height;
    const mergedDataZoom = {
      ...dataZoom,
      height: dataZoom.height ?? tokenDataZoomHeight
    };
    const domElements = helpers.createDomElements(
      util as unknown as Utils,
      token as TokenStyles,
      mergedDataZoom as DataZoomConfig
    );
    helpers.setupDomStructure($dom, domElements);

    // 设置标签样式和数据
    const tagToken = helpers.setupTagToken(util as unknown as Utils, token as TokenStyles);
    const riseCycleList = helpers.createRiseCycleList(
      data as DataSource,
      from,
      to,
      concepts,
      tagToken,
      customEvent
    );

    // 处理股票和图例数据
    const selectedStockIndex = 0;
    let selectedStock: StockData = stocks[selectedStockIndex];

    // 创建图例数据
    const legendData = helpers.createLegendData(
      data as DataSource,
      concepts,
      colorMap,
      util as unknown as Utils,
      ui as unknown as UI
    );

    // 组件实例
    let legendIns: LegendInstance;
    let kLineTooltipIns: KLineTooltipInstance;
    let hxKLine: HXKLineInstance;

    // 初始化状态
    let isFirstInit = true;
    let dataList: HXKLineData[];
    let xAxisData: string[] = [new Date().toISOString(), new Date().toISOString()];

    // 初始化DataZoom
    const dataZoomIns = new ui.CustomDataZoom(domElements.$dataZoomContainer, {
      data: xAxisData,
      token,
      // @ts-expect-error - DataZoomConfig extends the standard type to support string startValue
      style: dataZoom
    });

    // 初始化Tooltip
    if (showTooltip) {
      kLineTooltipIns = new ui.KLineTooltip(domElements.$tooltipContainer, {
        token
      });
    }

    // 应用数据缩放
    function applyDataZoom(startIndex: number, endIndex: number) {
      if (!dataList) {
        return;
      }

      const sliceData = dataList.slice(startIndex, endIndex + 1);
      hxKLine.applyNewData(sliceData, false, () => {
        hxKLine.zoomToShowAllData();
      });
    }

    // 处理首次初始化
    function handleFirstInit(kLineData: HXKLineCallbackData) {
      const chartDataList = kLineData.data;
      xAxisData = chartDataList.map(item => {
        return util.data.formatDate(item.timestamp);
      });

      // 将 startValue 从日期字符串转换为索引
      let initialStartIndex = 0;
      let initialEndIndex = xAxisData.length - 1;

      const { startValue, endValue } = mergedDataZoom;
      if (typeof startValue !== 'undefined' && typeof startValue === 'string') {
        const startIdx = xAxisData.indexOf(startValue);
        if (startIdx !== -1) {
          initialStartIndex = startIdx;
        }
      }

      if (typeof endValue !== 'undefined') {
        initialEndIndex = Math.min(endValue, xAxisData.length - 1);
      }

      // 更新 DataZoom 数据
      dataZoomIns.update(xAxisData);

      // 手动应用初始显示范围到 HXKline
      if (initialStartIndex > 0 || initialEndIndex < xAxisData.length - 1) {
        applyDataZoom(initialStartIndex, initialEndIndex);
      }

      isFirstInit = false;
      customEvent?.(data.values[data.values.length - 1]);
    }

    // 显示K线图Tooltip - 内部版本
    function internalShowKLineTooltip(kLineIns: unknown, dataIndex_?: number) {
      helpers.showKLineTooltip(
        kLineIns,
        hook as unknown as Hook,
        layout,
        kLineTooltipIns,
        util.const.LAYOUT as { SMALL: string; SMALL_1_2: string },
        dataIndex_
      );
    }

    // 订阅十字线变化事件
    function subscribeToTooltipEvents(kLineInstance: HXKLineInstance) {
      kLineInstance.subscribeAction('onCrosshairChange', crossData => {
        if (!crossData.kLineData) {
          return;
        }
        internalShowKLineTooltip(kLineInstance, crossData.dataIndex);
      });
    }

    // HXKLine完成初始化回调
    function onHXKLineComplete(status: string, kLineData: HXKLineCallbackData) {
      if (status === 'success') {
        helpers.configureHXKLine(hxKLine);
        dataList = kLineData.data;

        if (isFirstInit) {
          handleFirstInit(kLineData);
        }

        // 创建标签覆盖
        hxKLine.createOverlay({
          name: 'tag',
          extendData: riseCycleList,
          points: [{}]
        });

        // 处理DOM相关问题
        helpers.optimizeDomElements(domElements.$kline);

        // 初始tooltip展示最近日期的数据
        internalShowKLineTooltip(hxKLine);

        // 设置主图参数
        helpers.configureMainPane(hxKLine);

        // 订阅十字线变化事件
        subscribeToTooltipEvents(hxKLine);
      }
    }
    // 初始化HXKLine
    function initHXKLine() {
      return HXKline.initChart(
        domElements.$kline.getAttribute('id') ?? '',
        {
          id: util.common.generateUID(),
          code: selectedStock.code,
          market: selectedStock.market,
          data_class: 'kline',
          time_period: 'day_1',
          trade_date:
            isFirstInit && typeof tradeDate === 'undefined'
              ? 0
              : [new Date(tradeDate![0]), new Date(tradeDate![1])],
          adjust_type: 'forward',
          subscribe_type: 'noUpdate',
          isForbiddenLoadMore: true,
          isForbiddenUpdate: true
        },
        {
          chartType: '',
          dateFormat: 'YYYY-MM-DD',
          locale: 'zh_CN',
          styles: ['customTheme', tagToken?.option]
        },
        onHXKLineComplete
      );
    }

    // 渲染K线图
    function renderHXKLine() {
      if (hxKLine) {
        helpers.disposeHXKLine(hxKLine);
      }

      hxKLine = initHXKLine();
    }

    // 设置图例
    function setupLegend() {
      if (!showLegend) {
        return;
      }

      const convertedLegendData = legendData.map(item => ({
        ...item,
        type: typeof item.type === 'string' ? ui.Legend.ShowType.Line : item.type
      }));

      legendIns = new ui.Legend.Legend(domElements.$toolbarLegend, {
        data: convertedLegendData,
        layout: 'pagination',
        token
      });

      if (legendIns?.klineDropdown?.[0]) {
        legendIns.klineDropdown[0].on('change', (event: { key: string }) => {
          selectedStock = stocks.find(item => item.code === event.key) ?? stocks[0];
          renderHXKLine();
        });
      }
    }

    // 设置事件监听
    function setupEventListeners() {
      // 数据缩放事件处理
      dataZoomIns.on('dataZoom', (startIndex: number, endIndex: number) => {
        applyDataZoom(startIndex, endIndex);
      });

      dataZoomIns.on('valueRange', (valueRange: number[]) => {
        applyDataZoom(valueRange[0], valueRange[1]);
      });
    }

    // 初始化
    function initialize() {
      setupLegend();
      setupEventListeners();
      renderHXKLine();
    }

    // 开始渲染
    initialize();

    // 返回清理函数
    return () => {
      legendIns?.destroy?.();
      kLineTooltipIns?.destroy?.();
      dataZoomIns?.destroy?.();

      if (hxKLine) {
        HXKline.disposeChart(domElements.$kline.getAttribute('id'), () => {
          // HXKLine destroyed
        });
      }

      $dom.innerHTML = '';
    };
  }
});
